import { sisaHariKerja, todayIso } from "./hariKerja";
import { STATUS_KESELURUHAN } from "./auditStorage";
import { buildWATemplate, buildWAPenyerahanTim, JENIS_SEQUENCE } from "../data/templatePesan";

/**
 * For each active kasus, determine which jenis notif should have fired by today
 * but hasn't yet been recorded in riwayatNotifikasi. Creates PENDING entries for those.
 * Also auto-updates statusKeseluruhan to MELEWATI_BATAS when H+1 fires.
 *
 * Returns: { kasusListUpdated, inAppNotifs }
 *   kasusListUpdated = kasus list with new riwayatNotifikasi entries (status PENDING)
 *   inAppNotifs      = array of in-app notif objects to push into irbansus_notifications
 */
export function run60HKEngine({ kasusList, users, hariLibur }) {
  const today = todayIso();
  const updated = [];
  const inApp = [];

  for (const original of kasusList) {
    let kasus = { ...original, riwayatNotifikasi: [...(original.riwayatNotifikasi || [])] };
    if (!kasus.deadlineTL60HK) {
      updated.push(kasus);
      continue;
    }
    if (kasus.statusKeseluruhan === STATUS_KESELURUHAN.SELESAI) {
      updated.push(kasus);
      continue;
    }

    const sisa = sisaHariKerja(today, kasus.deadlineTL60HK, hariLibur);

    for (const jenis of JENIS_SEQUENCE) {
      if (!shouldFire(jenis, sisa)) continue;
      const already = kasus.riwayatNotifikasi.some((r) => r.jenis === jenis && r.statusKirim !== "DIBATALKAN");
      if (already) continue;

      // Build recipients
      const entries = buildEntriesForJenis({ jenis, kasus, users, sisa });
      kasus.riwayatNotifikasi.push(...entries);

      // In-app notifs (scope-aware entries for bell/Notifikasi page)
      const inAppEntries = buildInAppNotifs({ jenis, kasus, users });
      inApp.push(...inAppEntries);

      // H+1 auto-escalate
      if (jenis === "H1" && kasus.statusKeseluruhan !== STATUS_KESELURUHAN.MELEWATI_BATAS) {
        kasus.statusKeseluruhan = STATUS_KESELURUHAN.MELEWATI_BATAS;
      }
    }

    updated.push(kasus);
  }

  return { kasusListUpdated: updated, inAppNotifs: inApp };
}

function shouldFire(jenis, sisa) {
  switch (jenis) {
    case "T0":
      return true; // fires as soon as deadline exists
    case "H15":
      return sisa <= 15;
    case "H7":
      return sisa <= 7;
    case "H3":
      return sisa <= 3;
    case "H0":
      return sisa <= 0;
    case "H1":
      return sisa <= -1;
    default:
      return false;
  }
}

function buildEntriesForJenis({ jenis, kasus, users, sisa }) {
  const entries = [];
  const now = new Date().toISOString();

  // Terperiksa → WA + EMAIL (for jenis T0, H15, H7, H3, H0; H1 → terperiksa email only)
  const terperiksaRecipients =
    jenis === "H1" ? (kasus.terperiksa || []).slice(0) : (kasus.terperiksa || []).slice(0);
  const sendWAToTerperiksa = ["T0", "H15", "H7", "H3", "H0"].includes(jenis);
  const sendEmailToTerperiksa = true;

  for (const t of terperiksaRecipients) {
    const pesan = buildWATemplate({ jenis, kasus, terperiksa: t, sisaHK: sisa });
    if (sendWAToTerperiksa && t.noWA) {
      entries.push({
        id: `rn-${kasus.id}-${jenis}-WA-${t.namaPejabat || "x"}-${Date.now().toString(36)}`,
        tanggal: now,
        jenis,
        penerima: `${t.namaPejabat || "-"} (${t.namaInstansi || "-"})`,
        target: t.noWA,
        media: "WA",
        statusKirim: "PENDING",
        pesanTerkirim: pesan,
      });
    }
    if (sendEmailToTerperiksa && t.email) {
      entries.push({
        id: `rn-${kasus.id}-${jenis}-EMAIL-${t.namaPejabat || "x"}-${Date.now().toString(36)}-${Math.random()
          .toString(36)
          .slice(2, 6)}`,
        tanggal: now,
        jenis,
        penerima: `${t.namaPejabat || "-"} (${t.namaInstansi || "-"})`,
        target: t.email,
        media: "EMAIL",
        statusKirim: "DRAFT_READY",
        pesanTerkirim: pesan,
      });
    }
  }

  // Tambahkan notif WA otomatis untuk Tim Pemeriksa
  const sendWAToTim = ["T0", "H15", "H7", "H3", "H0"].includes(jenis);
  if (sendWAToTim) {
    const timIds = kasus.timPemeriksa || [];
    for (const uid of timIds) {
      const u = users.find(x => x.id === uid);
      if (u && u.noWA) {
        // Karena template WAPenyerahanTim sudah ada info sisaHK-nya jika kita ubah sedikit, atau gunakan buildWATemplate khusus tim.
        // Di sini kita pakai buildWAPenyerahanTim tapi pesannya disesuaikan dgn `jenis` reminder.
        // Untuk amannya, kita buat template khusus reminder tim jika diperlukan. Namun kita bisa buat teks custom di sini.
        let peranStr = u.role?.replace("_", " ") || "Tim";
        let pesanTim = `[SISTEM IRBAN - REMINDER ${jenis}]\n\nYth. Bapak/Ibu ${u.name} (${peranStr}),\n\nKami mengingatkan bahwa LHP No: ${kasus.nomorLHP}\nJudul: ${kasus.judulKasus}\n\nSaat ini sisa waktu tindak lanjut adalah *${sisa} Hari Kerja*.\nStatus saat ini: ${kasus.statusKeseluruhan}.\n\nMohon untuk memantau progres tindak lanjut. Terima kasih.`;
        
        entries.push({
          id: `rn-${kasus.id}-${jenis}-WA-TIM-${u.id}-${Date.now().toString(36)}`,
          tanggal: now,
          jenis,
          penerima: `${u.name} (${peranStr})`,
          target: u.noWA,
          media: "WA",
          statusKirim: "PENDING",
          pesanTerkirim: pesanTim,
        });
      }
    }
  }

  return entries;
}

function buildInAppNotifs({ jenis, kasus, users }) {
  const now = new Date().toISOString();
  const ketua = kasus.timPemeriksa?.map((id) => users.find((u) => u.id === id)).filter(Boolean) || [];

  // Recipient matrix by jenis → role targets
  let scope = { dalnisId: null, ketuaId: null, anggotaIds: [], toIrban: false };
  const tim = ketua; // full tim pemeriksa list as in-app recipients

  // ADMIN boleh merangkap slot operasional mana pun. timPemeriksa hanya menyimpan
  // id user tanpa keterangan slot, jadi kalau tidak ada pemegang peran khusus,
  // ADMIN yang ada di tim dipakai sebagai penerima. Tanpa ini notifikasi
  // Ketua/Dalnis hilang begitu slot itu diisi ADMIN.
  const cariPeran = (peran) =>
    tim.find((u) => u.role === peran) || tim.find((u) => u.role === "ADMIN");

  switch (jenis) {
    case "T0":
      // tim pemeriksa (in-app)
      scope = { ...scope, anggotaIds: tim.map((u) => u.id) };
      break;
    case "H7":
      // Ketua + Dalnis (in-app)
      scope = {
        ...scope,
        ketuaId: cariPeran("KETUA_TIM")?.id || null,
        dalnisId: cariPeran("DALNIS")?.id || null,
      };
      break;
    case "H3":
      scope = {
        ...scope,
        ketuaId: cariPeran("KETUA_TIM")?.id || null,
        dalnisId: cariPeran("DALNIS")?.id || null,
        toIrban: true,
      };
      break;
    case "H0":
      scope = {
        ...scope,
        ketuaId: cariPeran("KETUA_TIM")?.id || null,
        dalnisId: cariPeran("DALNIS")?.id || null,
        anggotaIds: tim
          .filter((u) => u.role === "ANGGOTA_TIM" || u.role === "ADMIN")
          .map((u) => u.id),
        toIrban: true,
      };
      break;
    case "H1":
      // IRBAN only
      scope = { ...scope, toIrban: true };
      break;
    default:
      return [];
  }

  const baseMessage = {
    T0: `LHP ${kasus.nomorLHP} diterbitkan — tindak lanjut wajib dalam 60 HK.`,
    H7: `MENDESAK — 7 hari kerja tersisa untuk TL ${kasus.nomorLHP}.`,
    H3: `FINAL — 3 hari kerja tersisa untuk TL ${kasus.nomorLHP}. Risiko eskalasi.`,
    H0: `JATUH TEMPO HARI INI — TL ${kasus.nomorLHP} belum selesai.`,
    H1: `BATAS WAKTU TERLEWAT — TL ${kasus.nomorLHP} dieskalasi ke IRBAN.`,
  };

  return [
    {
      id: `AUDIT_${jenis}-${kasus.id}`,
      type: jenis === "H1" ? "ESKALASI_AUDIT" : jenis === "H0" || jenis === "H3" ? "TERLAMBAT" : "PERINGATAN",
      subId: kasus.id,
      programKode: "P11c",
      subNama: kasus.judulKasus,
      message: baseMessage[jenis] || `Notifikasi TL ${kasus.nomorLHP}`,
      createdAt: now,
      scope,
      read: false,
      __audit: true,
    },
  ];
}
